import { defineComponent, ref, watchEffect, nextTick, computed } from 'vue'
import { dateIsInvalid, formatDate, parseDate, parseDateInput, formatDateToDDMMYYYY } from '@/utils/formatting/date'
import type { IDatepickerProps } from './types'
import { onClickOutside } from '@vueuse/core'
// @ts-ignore
import Datepicker from 'flowbite-datepicker/Datepicker'
import type { VNode } from 'vue'
import styles from './datepicker.module.css'
import { useResponsive } from '~/composables/useResponsive'

const DEFAULT_PLACEHOLDER = "DD/MM/YYYY"

const safeDate = (x: any) => {
  const d = parseDate(x)
  if (dateIsInvalid(x)) return new Date()
  return d
}

const datepicker = defineComponent<IDatepickerProps>({
  setup(props) {
    const { placeholder, name } = props
    const datepickerInstance = ref<Datepicker | null>(null)
    const wrapper = ref<VNode>()
    const inputRef = ref<VNode>()
    const svgWrapper = ref<VNode>()
    const dropdown = ref<HTMLElement | null>(null)
    const visible = ref<boolean>(false);

    const isUpdatingFromInput = ref(false)
    const isInputFocused = ref(false)
    const datepickerWatchStop = ref<(() => void) | null>(null)
    const changeDateHandler = ref<((e: any) => void) | null>(null)
    const isReadOnly = computed(() => props.locked || props.disabled)

    const datepickerElementRef = ref<HTMLElement | null>(null)
    const wrapperElementRef = ref<HTMLElement | null>(null)
    const inputElementRef = ref<HTMLInputElement | null>(null)

    const responsive = useResponsive();

    const updateValue = (val: Date) => {
      if (props.dataRef) {
        props.dataRef.setValue(safeDate(val));
      }
      if (props.fieldRef && props.reportChange) {
        props.reportChange(props.fieldRef, safeDate(val));
      }
    }

    const handleDateChange = (value: Date, close?: boolean) => {
      if (isReadOnly.value) return
      updateValue(value)
      if (close) visible.value = false
    }

    const updateDropdownRef = () => {
      nextTick(() => {
        const container = datepickerElementRef.value || wrapperElementRef.value
        const picker = container?.querySelector('.datepicker-dropdown')
        if (picker) {
          dropdown.value = picker as HTMLElement
        }
      })
    }

    const parseDateFromAttr = (attrValue: string | null): Date | null => {
      if (!attrValue) return null
      const parsed = parseDateInput(attrValue) || parseDate(attrValue)
      return dateIsInvalid(parsed) ? null : parsed
    }

    const getMinMaxDates = (): { minDate: Date | null, maxDate: Date } => {
      const input = inputElementRef.value
      if (!input) {
        return {
          minDate: props.minDate || null,
          maxDate: props.maxDate || new Date()
        }
      }

      const minDateAttr = input.getAttribute('datepicker-min-date')
      const maxDateAttr = input.getAttribute('datepicker-max-date')

      const minDate = parseDateFromAttr(minDateAttr) || props.minDate || null
      const maxDate = parseDateFromAttr(maxDateAttr) || props.maxDate || new Date()

      return { minDate, maxDate }
    }

    const handleMount = (node: VNode) => {
      const datepickerEl = node.el as HTMLElement
      datepickerElementRef.value = datepickerEl
      const { minDate, maxDate } = getMinMaxDates()

      datepickerInstance.value = new Datepicker(datepickerEl, {
        autohide: true,
        format: {
          toValue(date: Date) {
            return parseDate(date)
          },
          toDisplay(date: Date) {
            return formatDate(date)
          }
        },
        minDate: minDate || undefined,
        maxDate,
        defaultViewDate: safeDate(props.value),
        todayHighlight: true,
        daysOfweekDisabled: [],
        datesDisabled: [],
        orientation: 'bottom',
        weekStart: 1
      })

      updateDropdownRef()

      datepickerWatchStop.value = watchEffect(() => {
        if (isUpdatingFromInput.value) return
        if (datepickerInstance.value) {
          datepickerInstance.value.setDate(safeDate(props.value))
        }
      })

      changeDateHandler.value = (e: any) => {
        if (isReadOnly.value) return
        visible.value = false
        if (e.detail.date) {
          handleDateChange(new Date(e.detail.date), true)
        }
      }

      datepickerEl?.addEventListener('changeDate', changeDateHandler.value)
    }

    const handleUnmount = () => {
      if (datepickerWatchStop.value) {
        datepickerWatchStop.value()
        datepickerWatchStop.value = null
      }
      if (datepickerElementRef.value && changeDateHandler.value) {
        datepickerElementRef.value.removeEventListener('changeDate', changeDateHandler.value)
        changeDateHandler.value = null
      }
      if (datepickerInstance.value) {
        datepickerInstance.value.destroy()
        datepickerInstance.value = null
      }
      datepickerElementRef.value = null
    }

    const handleWrapperMount = (node: VNode) => {
      wrapperElementRef.value = node.el as HTMLElement
      updateDropdownRef()
    }

    const handleInputClick = () => {
      if (!isReadOnly.value && !visible.value) {
        visible.value = true
        updateDropdownRef()
      }
    }

    const handlePickerClick = (e: Event) => {
      e.stopPropagation()
    }

    onClickOutside(
      wrapperElementRef,
      () => {
        if (!isReadOnly.value && visible.value) {
          visible.value = false
        }
      },
      {
        ignore: [
          inputElementRef,
          dropdown
        ]
      }
    )

    const handleInputChange = async (e: Event | KeyboardEvent | FocusEvent) => {
      if (isReadOnly.value) return
      const inputValue = (e.target as HTMLInputElement).value
      if (!inputValue) return

      const parsed = parseDateInput(inputValue) || parseDate(inputValue)
      if (!parsed || dateIsInvalid(parsed)) return

      const validDate = parsed as Date
      const { minDate, maxDate } = getMinMaxDates()
      const dateTime = validDate.getTime()
      if (minDate && dateTime < minDate.getTime()) return
      if (dateTime > maxDate.getTime()) return

      isUpdatingFromInput.value = true
      handleDateChange(validDate, false)
      await nextTick()

      datepickerInstance.value?.setDate(validDate)

      if (inputElementRef.value) {
        inputElementRef.value.value = (isInputFocused.value || visible.value)
          ? inputValue
          : formatDate(validDate)
      }

      isUpdatingFromInput.value = false
    }

    const handleInputKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleInputChange(e)
        inputElementRef.value?.blur()
      }
    }

    watchEffect(() => {
      if (!inputElementRef.value || isUpdatingFromInput.value || isReadOnly.value) return

      const date = safeDate(props.value)
      const isFocused = isInputFocused.value || visible.value
      const newValue = isFocused && !responsive.isTablet.value || !responsive.isPhone.value
        ? formatDateToDDMMYYYY(date)
        : formatDate(date)

      if (inputElementRef.value.value !== newValue) {
        inputElementRef.value.value = newValue
      }
    })

    const handleInputFocus = () => {
      if (responsive.isTablet.value || responsive.isPhone.value) return
      isInputFocused.value = true
    }

    const handleInputBlur = () => {
      if (responsive.isTablet.value || responsive.isPhone.value) return
      isInputFocused.value = false
    }

    const handleInputMount = (node: VNode) => {
      const inputElement = node.el as HTMLInputElement
      if (inputElement) {
        inputElementRef.value = inputElement
        inputElement.addEventListener('blur', handleInputBlur)
        inputElement.addEventListener('focus', handleInputFocus)
        inputElement.addEventListener('keydown', handleInputKeydown)
      }
    }

    const handleInputUnmount = (node: VNode) => {
      const inputElement = node.el as HTMLInputElement
      if (inputElement) {
        inputElement.removeEventListener('blur-sm', handleInputBlur)
        inputElement.removeEventListener('focus', handleInputFocus)
        inputElement.removeEventListener('keydown', handleInputKeydown)
      }
    }

    return () => {
      return (
        <div
          {...{ onVnodeMounted: handleWrapperMount }}
          ref={wrapper}
          id="datepicker-container"
          class={[
            styles.datepickerCustom,
            'bg-transparent relative p-0 border-none',
            isReadOnly.value ? 'opacity-50' : ''
          ].join(' ')}
        >
          <div
            ref={svgWrapper}
            class="absolute inset-y-0 inset-s-0 flex items-center ps-3.5"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              zIndex: '1'
            }}
          >
            <svg
              class="w-4 h-4 text-gray-500 dark:text-gray-400"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M20 4a2 2 0 0 0-2-2h-2V1a1 1 0 0 0-2 0v1h-3V1a1 1 0 0 0-2 0v1H6V1a1 1 0 0 0-2 0v1H2a2 2 0 0 0-2 2v2h20V4ZM0 18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8H0v10Zm5-8h10a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2Z" />
            </svg>
          </div>
          <input
            datepicker-input
            {...(responsive.isTablet.value || responsive.isPhone.value ? { readonly: true } : {})}
            class="bg-transparent relative cursor-pointer border border-gray-300 outline-hidden text-gray-900 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-sm block w-full ps-10 p-2.5"
            placeholder={placeholder?.toString() || DEFAULT_PLACEHOLDER}
            disabled={isReadOnly.value}
            name={name}
            onClick={handleInputClick}
            ref={inputRef}
            {...{ onVnodeMounted: handleInputMount, onVnodeBeforeUnmount: handleInputUnmount }}
          />

          {visible.value && (
            <div
              inline-datepicker
              {...{ onVnodeMounted: handleMount, onVnodeBeforeUnmount: handleUnmount }}
              onClick={handlePickerClick}
              style={{
                position: 'absolute',
                top: props.placement === 'top' ? '-340px' : '100%',
                left: 0,
                zIndex: '50',
                pointerEvents: 'all'
              }}
            />
          )}
        </div>
      )
    }
  }
})

datepicker.props = [
  'placeholder',
  'onChange',
  'onInput',
  'name',
  'value',
  'fieldRef',
  'dataRef',
  'reportChange',
  'locked',
  'disabled',
  'placement',
  'maxDate',
  'minDate'
]

export default datepicker
